import type { SxProps, Theme } from '@mui/material/styles';

// Matches the source site's computed body copy exactly: Manrope 16/400,
// 24px line-height, -0.16px letter-spacing, #04233C (== our text.primary).
// Every block below is its own MUI Typography (default variant "body1"),
// so each one must set these explicitly — inheriting from a shared parent
// isn't enough, since the body1 variant's own font values win over anything
// merely inherited.
const legalTextBase = (theme: Theme) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '1rem',
  lineHeight: '24px',
  letterSpacing: '-0.16px',
  color: theme.palette.text.primary,
});

export const legalPageStyles: Record<string, SxProps<Theme>> = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  },
  main: (theme) => ({
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    paddingInline: { xs: theme.spacing(3), md: theme.spacing(4) },
    paddingBlock: { xs: theme.spacing(4), md: theme.spacing(6) },
  }),
  content: {
    width: '100%',
    maxWidth: 760,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  title: (theme) => ({
    ...theme.typography.display,
    fontSize: { xs: '2rem', md: '2.5rem' },
    color: theme.palette.text.primary,
    margin: 0,
  }),
  updated: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  }),
  body: (theme) => ({
    ...legalTextBase(theme),
    fontWeight: 400,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  }),
  paragraph: (theme) => ({
    ...legalTextBase(theme),
    fontWeight: 400,
    margin: 0,
  }),
  // Bold inline lead-in at the start of a paragraph (e.g. "Personal
  // Identifiers: ...") — same computed style as body text, just weight 700.
  boldLead: (theme) => ({
    ...legalTextBase(theme),
    fontWeight: 700,
  }),
  emphasis: (theme) => ({
    ...legalTextBase(theme),
    fontWeight: 700,
    margin: 0,
  }),
  // Section headings on the source site are the same 16px body copy, just
  // bold — not a larger heading size.
  heading: (theme) => ({
    ...legalTextBase(theme),
    fontWeight: 700,
  }),
  subheading: (theme) => ({
    ...legalTextBase(theme),
    fontWeight: 700,
  }),
  list: {
    margin: 0,
    paddingLeft: '1.25rem',
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  listItem: (theme) => ({
    ...legalTextBase(theme),
    fontWeight: 400,
  }),
  // Inline email/URL mentions the source site renders as real anchors —
  // source CSS: `a { color: inherit; text-decoration: underline; }`, i.e.
  // the same dark body color, not a colored link.
  link: (theme) => ({
    ...legalTextBase(theme),
    fontWeight: 400,
    color: 'inherit',
    textDecoration: 'underline',
    '&:hover': { color: 'inherit' },
  }),
  linkBold: (theme) => ({
    ...legalTextBase(theme),
    fontWeight: 700,
    color: 'inherit',
    textDecoration: 'underline',
    '&:hover': { color: 'inherit' },
  }),
};
